import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { cn } from "@/utils/utils";
import { X } from "@phosphor-icons/react";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 bg-[#0E0E0E] shadow-lg pt-[5.5rem] transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  onClose?: () => void;
}

const defaultPreventCloseFn = () => false;
const SheetPreventCloseContext = React.createContext<
  React.MutableRefObject<() => boolean>
>({ current: defaultPreventCloseFn });

export const usePreventSheetClose = (fn: () => boolean) => {
  const preventSheetCloseCallbackRef = React.useContext(
    SheetPreventCloseContext,
  );
  const fnRef = React.useRef(fn);
  fnRef.current = fn;

  React.useEffect(() => {
    preventSheetCloseCallbackRef.current = fnRef.current;

    // reset callback when component unmounts
    return () => {
      preventSheetCloseCallbackRef.current = defaultPreventCloseFn;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  const preventSheetCloseCallbackRef = React.useRef(defaultPreventCloseFn);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const handleCloseSheet = (
    e: KeyboardEvent | CustomEvent<{ originalEvent: PointerEvent }>,
  ) => {
    if (preventSheetCloseCallbackRef.current()) {
      e.preventDefault();
      setIsDialogOpen(true);
    } else {
      props.onClose?.();
    }
  };

  const onCancel = () => {
    setIsDialogOpen(false);
  };

  const onContinue = () => {
    preventSheetCloseCallbackRef.current = defaultPreventCloseFn;
    props.onClose?.();
  };

  return (
    <SheetPreventCloseContext.Provider value={preventSheetCloseCallbackRef}>
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={ref}
          onEscapeKeyDown={handleCloseSheet}
          onPointerDownOutside={handleCloseSheet}
          className={cn(
            sheetVariants({ side }),
            className,
            "flex h-full min-w-[450px] flex-col focus-visible:outline-none",
          )}
          {...props}
        >
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent
              onOpenAutoFocus={(e) => e.preventDefault()}
              onClose={onCancel}
            >
              <DialogHeader>
                <DialogTitle>Unfinished Changes</DialogTitle>
              </DialogHeader>
              <DialogDescription>
                You have may have unsaved changes.
              </DialogDescription>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onContinue}
                    className="min-w-[140px]"
                  >
                    Discard Changes
                  </Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="default"
                    onClick={onCancel}
                    className="min-w-[140px]"
                  >
                    Keep Editing
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {children}
          <SheetPrimitive.Close
            onClick={() => {
              if (props.onClose) {
                props.onClose();
              }
            }}
            className="absolute right-sheet-gap top-sheet-gap rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-secondary hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="size-6" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    </SheetPreventCloseContext.Provider>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mx-sheet-gap mb-6 flex flex-col", className)}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetActions = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("absolute bottom-0 right-0 w-full p-sheet-gap", className)}
    {...props}
  />
);
SheetActions.displayName = "SheetActions";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-body-2xl font-medium text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetActions,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
